export interface SpecialFactoryProduct {
  id: number;
  name: string;
  price: number;
  priceUnit: string;
  minOrder: number;
  minOrderUnit: string;
  image: string;
  category: string;
  categoryIcon: string;
  seller: string;
  sellerFlag: string;
  sellerCountry: string;
  factoryAddress: string;
  sold: number;
  stock: number;
  description: string;
  rating: number;
  reviews: number;
  badge: string;
  specs: string[];
}

export const specialFactoryCategories = [
  { id: "solar-panel", label: "Solar Panels", icon: "ri-sun-line", color: "bg-amber-50 text-amber-600 border-amber-200" },
  { id: "solar-battery", label: "Solar Batteries", icon: "ri-battery-charge-line", color: "bg-green-50 text-green-600 border-green-200" },
  { id: "solar-inverter", label: "Solar Inverters", icon: "ri-flashlight-line", color: "bg-yellow-50 text-yellow-600 border-yellow-200" },
  { id: "golf-cart", label: "Golf Carts", icon: "ri-car-line", color: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  { id: "motorcycle", label: "Motorcycles", icon: "ri-motorbike-line", color: "bg-red-50 text-red-600 border-red-200" },
  { id: "scooter-ebike", label: "Scooters & E-Bikes", icon: "ri-e-bike-line", color: "bg-sky-50 text-sky-600 border-sky-200" },
  { id: "sport-clothes", label: "Sport Clothes", icon: "ri-shirt-line", color: "bg-rose-50 text-rose-600 border-rose-200" },
  { id: "garden-tools", label: "Garden Tools", icon: "ri-plant-line", color: "bg-lime-50 text-lime-600 border-lime-200" },
  { id: "hose-nozzle", label: "Brass Hose Nozzles", icon: "ri-drop-line", color: "bg-teal-50 text-teal-600 border-teal-200" },
  { id: "faucet", label: "Sink & Bath Faucets", icon: "ri-water-flash-line", color: "bg-cyan-50 text-cyan-600 border-cyan-200" },
  { id: "welding-cable", label: "Welding Cables", icon: "ri-plug-line", color: "bg-orange-50 text-orange-600 border-orange-200" },
];

export const specialFactoryProducts: SpecialFactoryProduct[] = [
  // Solar Panels
  {
    id: 101,
    name: "Monocrystalline Solar Panel 400W High Efficiency",
    price: 89,
    priceUnit: "pc",
    minOrder: 10,
    minOrderUnit: "pcs",
    image: "https://public.readdy.ai/ai/img_res/7afb188ec3f3ed62ad708abc88dd6247.jpg",
    category: "solar-panel",
    categoryIcon: "ri-sun-line",
    seller: "Yingli Green Energy Co.",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.3 Yingli Road, Baoding, Hebei, China",
    sold: 8420,
    stock: 5000,
    description: "High-efficiency monocrystalline solar panel with 21.5% conversion rate. Ideal for residential and commercial rooftop installations. Anti-reflective coating, IP68 junction box.",
    rating: 4.9,
    reviews: 1243,
    badge: "Top Seller",
    specs: ["400W Output", "21.5% Efficiency", "25-Year Warranty", "IP68 Rated"],
  },
  {
    id: 102,
    name: "Bifacial Solar Panel 550W Dual Glass",
    price: 128,
    priceUnit: "pc",
    minOrder: 5,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=bifacial%20solar%20panel%20550W%20dual%20glass%20transparent%20back%20sheet%20photovoltaic%20module%20clean%20light%20gray%20background%20professional%20product%20photography%20solar%20energy&width=400&height=300&seq=sfp102&orientation=landscape",
    category: "solar-panel",
    categoryIcon: "ri-sun-line",
    seller: "Longi Solar Technology",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.1 Longi Avenue, Xi'an, Shaanxi, China",
    sold: 5210,
    stock: 3000,
    description: "Bifacial solar panel captures sunlight from both sides, increasing energy yield by up to 30%. Dual glass design for superior durability and weather resistance.",
    rating: 4.8,
    reviews: 876,
    badge: "Hot",
    specs: ["550W Output", "Bifacial Design", "30% More Yield", "Dual Glass"],
  },
  {
    id: 103,
    name: "Flexible Solar Panel 200W Lightweight",
    price: 65,
    priceUnit: "pc",
    minOrder: 20,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=flexible%20thin%20film%20solar%20panel%20200W%20lightweight%20bendable%20surface%20clean%20white%20background%20professional%20product%20photography%20portable%20solar%20energy%20camping&width=400&height=300&seq=sfp103&orientation=landscape",
    category: "solar-panel",
    categoryIcon: "ri-sun-line",
    seller: "Sunpower Flex Tech",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "Zone C, Shenzhen Hi-Tech Park, Guangdong, China",
    sold: 3100,
    stock: 8000,
    description: "Ultra-lightweight flexible solar panel for RVs, boats, and curved surfaces. Only 2.8kg, can bend up to 30 degrees. ETFE surface for maximum durability.",
    rating: 4.7,
    reviews: 542,
    badge: "New",
    specs: ["200W Output", "2.8kg Weight", "30° Bendable", "ETFE Surface"],
  },
  // Solar Batteries
  {
    id: 201,
    name: "LiFePO4 Battery 100Ah 48V Solar Storage",
    price: 380,
    priceUnit: "pc",
    minOrder: 2,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=LiFePO4%20lithium%20iron%20phosphate%20battery%20100Ah%2048V%20solar%20energy%20storage%20system%20clean%20white%20background%20professional%20product%20photography%20green%20energy&width=400&height=300&seq=sfp201&orientation=landscape",
    category: "solar-battery",
    categoryIcon: "ri-battery-charge-line",
    seller: "CATL Battery Solutions",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.2 CATL Road, Ningde, Fujian, China",
    sold: 4320,
    stock: 2000,
    description: "Grade A LiFePO4 cells, 6000+ cycle life, built-in BMS protection. Perfect for off-grid solar systems, home energy storage, and commercial applications.",
    rating: 4.9,
    reviews: 987,
    badge: "Top Seller",
    specs: ["100Ah Capacity", "6000+ Cycles", "Built-in BMS", "10-Year Life"],
  },
  {
    id: 202,
    name: "All-in-One Solar Battery 5kWh Wall Mount",
    price: 1250,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=all-in-one%20wall%20mounted%20solar%20battery%20storage%20system%205kWh%20sleek%20modern%20design%20clean%20white%20background%20professional%20product%20photography%20home%20energy%20storage&width=400&height=300&seq=sfp202&orientation=landscape",
    category: "solar-battery",
    categoryIcon: "ri-battery-charge-line",
    seller: "BYD Energy Storage",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "BYD Industrial Park, Shenzhen, Guangdong, China",
    sold: 2180,
    stock: 500,
    description: "Compact wall-mounted home energy storage system. 5kWh capacity, compatible with all major solar inverters. Smart app monitoring, IP55 rated for outdoor installation.",
    rating: 4.8,
    reviews: 634,
    badge: "Hot",
    specs: ["5kWh Capacity", "IP55 Rated", "App Monitoring", "10-Year Warranty"],
  },
  {
    id: 203,
    name: "Gel Deep Cycle Battery 200Ah 12V Solar",
    price: 145,
    priceUnit: "pc",
    minOrder: 4,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=gel%20deep%20cycle%20battery%20200Ah%2012V%20solar%20system%20maintenance%20free%20clean%20white%20background%20professional%20product%20photography%20energy%20storage&width=400&height=300&seq=sfp203&orientation=landscape",
    category: "solar-battery",
    categoryIcon: "ri-battery-charge-line",
    seller: "Ritar Power Group",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.8 Ritar Road, Shenzhen, Guangdong, China",
    sold: 6700,
    stock: 10000,
    description: "Maintenance-free gel deep cycle battery for solar off-grid systems. Superior performance in extreme temperatures, vibration resistant, 800+ cycle life.",
    rating: 4.6,
    reviews: 1120,
    badge: "",
    specs: ["200Ah Capacity", "Maintenance Free", "800+ Cycles", "Gel Technology"],
  },
  // Solar Inverters
  {
    id: 301,
    name: "Hybrid Solar Inverter 5kW MPPT On/Off Grid",
    price: 420,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=hybrid%20solar%20inverter%205kW%20MPPT%20on%20off%20grid%20system%20clean%20white%20background%20professional%20product%20photography%20power%20electronics%20renewable%20energy&width=400&height=300&seq=sfp301&orientation=landscape",
    category: "solar-inverter",
    categoryIcon: "ri-flashlight-line",
    seller: "Growatt New Energy",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.28 Guangming Road, Shenzhen, Guangdong, China",
    sold: 5430,
    stock: 1500,
    description: "All-in-one hybrid inverter with built-in MPPT solar charger. Supports on-grid, off-grid, and backup modes. WiFi monitoring, compatible with lithium and lead-acid batteries.",
    rating: 4.8,
    reviews: 892,
    badge: "Top Seller",
    specs: ["5kW Output", "MPPT Charger", "WiFi Monitoring", "3-Year Warranty"],
  },
  {
    id: 302,
    name: "String Solar Inverter 10kW Three Phase",
    price: 680,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=string%20solar%20inverter%2010kW%20three%20phase%20commercial%20industrial%20clean%20white%20background%20professional%20product%20photography%20power%20conversion%20system&width=400&height=300&seq=sfp302&orientation=landscape",
    category: "solar-inverter",
    categoryIcon: "ri-flashlight-line",
    seller: "Sungrow Power Supply",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.1 Sungrow Road, Hefei, Anhui, China",
    sold: 3210,
    stock: 800,
    description: "Commercial-grade three-phase string inverter for large solar installations. 98.4% peak efficiency, IP66 protection, integrated DC switch, remote monitoring via cloud.",
    rating: 4.9,
    reviews: 567,
    badge: "Hot",
    specs: ["10kW Output", "98.4% Efficiency", "IP66 Rated", "Cloud Monitoring"],
  },
  {
    id: 303,
    name: "Micro Inverter 600W Per Panel Optimization",
    price: 95,
    priceUnit: "pc",
    minOrder: 10,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=micro%20inverter%20600W%20small%20compact%20solar%20panel%20optimization%20device%20clean%20white%20background%20professional%20product%20photography%20power%20electronics&width=400&height=300&seq=sfp303&orientation=landscape",
    category: "solar-inverter",
    categoryIcon: "ri-flashlight-line",
    seller: "APsystems Microinverter",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.5 APsystems Park, Chengdu, Sichuan, China",
    sold: 7800,
    stock: 5000,
    description: "Panel-level power optimization with individual MPPT per panel. Eliminates shading losses, real-time monitoring per panel, 25-year warranty, plug-and-play installation.",
    rating: 4.7,
    reviews: 1340,
    badge: "New",
    specs: ["600W Per Panel", "25-Year Warranty", "Panel Monitoring", "Plug & Play"],
  },
  // Golf Carts
  {
    id: 401,
    name: "Electric Golf Cart 4-Seater 48V Lithium",
    price: 4800,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=electric%20golf%20cart%204%20seater%20white%20clean%20modern%20design%20lithium%20battery%20powered%20on%20clean%20light%20background%20professional%20product%20photography%20vehicle&width=400&height=300&seq=sfp401&orientation=landscape",
    category: "golf-cart",
    categoryIcon: "ri-car-line",
    seller: "Dongguan Golf Cart Co.",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.88 Golf Industrial Zone, Dongguan, Guangdong, China",
    sold: 1240,
    stock: 200,
    description: "Premium 4-seater electric golf cart with 48V lithium battery. 80km range per charge, max speed 35km/h, LED headlights, USB charging ports, foldable windshield.",
    rating: 4.8,
    reviews: 342,
    badge: "Top Seller",
    specs: ["4-Seater", "80km Range", "48V Lithium", "35km/h Max"],
  },
  {
    id: 402,
    name: "Golf Cart 6-Seater Club Car Style 72V",
    price: 6500,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=golf%20cart%206%20seater%20club%20car%20style%20electric%20vehicle%20resort%20hotel%20clean%20white%20background%20professional%20product%20photography%20luxury%20transport&width=400&height=300&seq=sfp402&orientation=landscape",
    category: "golf-cart",
    categoryIcon: "ri-car-line",
    seller: "Suzhou EV Transport",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.12 EV Park, Suzhou, Jiangsu, China",
    sold: 680,
    stock: 100,
    description: "6-seater resort-grade golf cart for hotels, resorts, and large golf courses. 72V system, 120km range, rear-facing back seats, custom color options, CE certified.",
    rating: 4.7,
    reviews: 198,
    badge: "Hot",
    specs: ["6-Seater", "120km Range", "72V System", "CE Certified"],
  },
  {
    id: 403,
    name: "Utility Golf Cart 2-Seater with Cargo Box",
    price: 3200,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=utility%20golf%20cart%202%20seater%20cargo%20box%20electric%20work%20vehicle%20clean%20white%20background%20professional%20product%20photography%20industrial%20transport&width=400&height=300&seq=sfp403&orientation=landscape",
    category: "golf-cart",
    categoryIcon: "ri-car-line",
    seller: "Guangzhou EV Works",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "Zone A, Guangzhou Auto Park, Guangdong, China",
    sold: 2100,
    stock: 300,
    description: "Workhorse utility cart with large rear cargo box (500kg capacity). Perfect for warehouses, farms, and resorts. 48V, 60km range, hydraulic dump bed option available.",
    rating: 4.6,
    reviews: 421,
    badge: "",
    specs: ["500kg Cargo", "60km Range", "Hydraulic Dump", "48V System"],
  },
  // Motorcycles
  {
    id: 501,
    name: "Electric Motorcycle 72V 5000W Sport Style",
    price: 1850,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=electric%20motorcycle%2072V%20sport%20style%20sleek%20modern%20design%20black%20red%20clean%20white%20background%20professional%20product%20photography%20EV%20two%20wheeler&width=400&height=300&seq=sfp501&orientation=landscape",
    category: "motorcycle",
    categoryIcon: "ri-motorbike-line",
    seller: "Chongqing Zongshen EV",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.1 Zongshen Road, Chongqing, China",
    sold: 3420,
    stock: 500,
    description: "High-performance electric motorcycle with 5000W brushless motor. 0-60km/h in 4.5 seconds, 150km range, regenerative braking, LCD dashboard, fast charging 4 hours.",
    rating: 4.8,
    reviews: 678,
    badge: "Hot",
    specs: ["5000W Motor", "150km Range", "4.5s 0-60", "Fast Charge"],
  },
  {
    id: 502,
    name: "Gas Motorcycle 250cc Street Bike EFI",
    price: 1200,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=250cc%20street%20motorcycle%20gas%20fuel%20injection%20sport%20bike%20clean%20white%20background%20professional%20product%20photography%20two%20wheeler%20vehicle&width=400&height=300&seq=sfp502&orientation=landscape",
    category: "motorcycle",
    categoryIcon: "ri-motorbike-line",
    seller: "Loncin Motor Co.",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.8 Loncin Avenue, Chongqing, China",
    sold: 5600,
    stock: 800,
    description: "250cc single-cylinder EFI street motorcycle. Fuel injection for reliable cold starts, disc brakes front and rear, LED lighting, digital instrument cluster, Euro 5 compliant.",
    rating: 4.7,
    reviews: 1120,
    badge: "Top Seller",
    specs: ["250cc EFI", "Euro 5", "Disc Brakes", "LED Lights"],
  },
  {
    id: 503,
    name: "Electric Dirt Bike 60V Off-Road Enduro",
    price: 2200,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=electric%20dirt%20bike%2060V%20off%20road%20enduro%20motocross%20green%20forest%20background%20professional%20product%20photography%20adventure%20sport%20motorcycle&width=400&height=300&seq=sfp503&orientation=landscape",
    category: "motorcycle",
    categoryIcon: "ri-motorbike-line",
    seller: "Surron Electric Bike",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.5 Surron Park, Chengdu, Sichuan, China",
    sold: 2890,
    stock: 400,
    description: "Off-road electric dirt bike with 60V 38Ah battery. 100km range on trails, 3000W mid-drive motor, adjustable suspension, hydraulic disc brakes, IP67 waterproof.",
    rating: 4.9,
    reviews: 543,
    badge: "New",
    specs: ["3000W Motor", "100km Range", "IP67 Rated", "Hydraulic Brakes"],
  },
  // Scooters & E-Bikes
  {
    id: 601,
    name: "Electric Scooter 48V 1000W Foldable",
    price: 380,
    priceUnit: "unit",
    minOrder: 5,
    minOrderUnit: "units",
    image: "https://readdy.ai/api/search-image?query=electric%20scooter%2048V%20foldable%20lightweight%20modern%20design%20clean%20white%20background%20professional%20product%20photography%20urban%20commute%20transport&width=400&height=300&seq=sfp601&orientation=landscape",
    category: "scooter-ebike",
    categoryIcon: "ri-e-bike-line",
    seller: "Xiaomi Ninebot OEM",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.9 Innovation Road, Beijing, China",
    sold: 12400,
    stock: 5000,
    description: "Foldable electric scooter for urban commuting. 45km range, max speed 25km/h, 10-inch pneumatic tires, regenerative braking, IP54 water resistance, app connectivity.",
    rating: 4.8,
    reviews: 3210,
    badge: "Top Seller",
    specs: ["45km Range", "25km/h Max", "10\" Tires", "App Control"],
  },
  {
    id: 602,
    name: "E-Bike 750W Mid-Drive Trekking",
    price: 1100,
    priceUnit: "unit",
    minOrder: 2,
    minOrderUnit: "units",
    image: "https://readdy.ai/api/search-image?query=electric%20bike%20750W%20mid%20drive%20trekking%20e-bike%20clean%20white%20background%20professional%20product%20photography%20urban%20cycling%20commute&width=400&height=300&seq=sfp602&orientation=landscape",
    category: "scooter-ebike",
    categoryIcon: "ri-e-bike-line",
    seller: "Bafang E-Bike Systems",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.3 Bafang Road, Suzhou, Jiangsu, China",
    sold: 4560,
    stock: 1000,
    description: "Trekking e-bike with 750W Bafang mid-drive motor. 120km range with pedal assist, Shimano 9-speed gears, hydraulic disc brakes, integrated rear rack, 27.5\" wheels.",
    rating: 4.9,
    reviews: 876,
    badge: "Hot",
    specs: ["750W Mid-Drive", "120km Range", "Shimano 9-Speed", "Hydraulic Brakes"],
  },
  {
    id: 603,
    name: "Fat Tire E-Bike 1000W All-Terrain",
    price: 1350,
    priceUnit: "unit",
    minOrder: 1,
    minOrderUnit: "unit",
    image: "https://readdy.ai/api/search-image?query=fat%20tire%20electric%20bike%201000W%20all%20terrain%20snow%20beach%20mountain%20e-bike%20clean%20white%20background%20professional%20product%20photography%20adventure%20cycling&width=400&height=300&seq=sfp603&orientation=landscape",
    category: "scooter-ebike",
    categoryIcon: "ri-e-bike-line",
    seller: "Addmotor E-Bike Factory",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.22 Addmotor Park, Guangzhou, Guangdong, China",
    sold: 3200,
    stock: 600,
    description: "All-terrain fat tire e-bike for snow, sand, and mountain trails. 1000W rear hub motor, 4-inch fat tires, 80km range, full suspension, 7-speed Shimano, LCD display.",
    rating: 4.7,
    reviews: 654,
    badge: "New",
    specs: ["1000W Motor", "4\" Fat Tires", "80km Range", "Full Suspension"],
  },
  // Sport Clothes
  {
    id: 701,
    name: "Men's Running Set Moisture-Wicking OEM",
    price: 8.5,
    priceUnit: "set",
    minOrder: 100,
    minOrderUnit: "sets",
    image: "https://readdy.ai/api/search-image?query=men%20running%20sport%20set%20moisture%20wicking%20shirt%20shorts%20athletic%20wear%20clean%20white%20background%20professional%20product%20photography%20sportswear%20wholesale&width=400&height=300&seq=sfp701&orientation=landscape",
    category: "sport-clothes",
    categoryIcon: "ri-shirt-line",
    seller: "Guangzhou Sport Apparel",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.55 Garment Street, Guangzhou, Guangdong, China",
    sold: 28000,
    stock: 50000,
    description: "OEM men's running set with moisture-wicking polyester fabric. Quick-dry technology, 4-way stretch, reflective strips, custom logo printing available, sizes XS-3XL.",
    rating: 4.7,
    reviews: 4320,
    badge: "Top Seller",
    specs: ["Moisture-Wicking", "4-Way Stretch", "Custom Logo", "XS-3XL Sizes"],
  },
  {
    id: 702,
    name: "Women's Yoga Leggings High Waist Seamless",
    price: 6.8,
    priceUnit: "pc",
    minOrder: 200,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=women%20yoga%20leggings%20high%20waist%20seamless%20athletic%20wear%20clean%20white%20background%20professional%20product%20photography%20fitness%20sportswear%20wholesale&width=400&height=300&seq=sfp702&orientation=landscape",
    category: "sport-clothes",
    categoryIcon: "ri-shirt-line",
    seller: "Yiwu Fashion Sport Co.",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.8 Fashion Ave, Yiwu, Zhejiang, China",
    sold: 45000,
    stock: 100000,
    description: "Seamless high-waist yoga leggings with tummy control. 80% nylon 20% spandex, squat-proof, moisture-wicking, available in 20+ colors, custom label and packaging.",
    rating: 4.8,
    reviews: 8760,
    badge: "Hot",
    specs: ["Seamless Knit", "Squat-Proof", "20+ Colors", "Custom Label"],
  },
  {
    id: 703,
    name: "Sports Jersey Football Soccer Custom Print",
    price: 4.2,
    priceUnit: "pc",
    minOrder: 50,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=sports%20jersey%20football%20soccer%20custom%20print%20team%20uniform%20clean%20white%20background%20professional%20product%20photography%20sportswear%20wholesale&width=400&height=300&seq=sfp703&orientation=landscape",
    category: "sport-clothes",
    categoryIcon: "ri-shirt-line",
    seller: "Pakistan Sports Apparel",
    sellerFlag: "🇵🇰",
    sellerCountry: "Pakistan",
    factoryAddress: "Sialkot Sports Zone, Punjab, Pakistan",
    sold: 62000,
    stock: 200000,
    description: "Custom sublimation printed sports jerseys for football, soccer, basketball. 100% polyester, breathable mesh panels, full custom design, name and number printing, 7-day production.",
    rating: 4.6,
    reviews: 12400,
    badge: "Top Seller",
    specs: ["Full Sublimation", "Custom Design", "7-Day Production", "All Sports"],
  },
  // Garden Tools
  {
    id: 801,
    name: "Cordless Lawn Mower 40V Brushless Motor",
    price: 185,
    priceUnit: "unit",
    minOrder: 5,
    minOrderUnit: "units",
    image: "https://readdy.ai/api/search-image?query=cordless%20lawn%20mower%2040V%20brushless%20motor%20electric%20garden%20tool%20clean%20white%20background%20professional%20product%20photography%20outdoor%20equipment&width=400&height=300&seq=sfp801&orientation=landscape",
    category: "garden-tools",
    categoryIcon: "ri-plant-line",
    seller: "Greenworks Tools Factory",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.1 Greenworks Road, Changsha, Hunan, China",
    sold: 5600,
    stock: 2000,
    description: "40V brushless cordless lawn mower with 40cm cutting width. 60-minute runtime, 6-position height adjustment, foldable handle for easy storage, mulching and bagging modes.",
    rating: 4.8,
    reviews: 1230,
    badge: "Top Seller",
    specs: ["40V Brushless", "40cm Cut Width", "60min Runtime", "6-Height Adjust"],
  },
  {
    id: 802,
    name: "Garden Tool Set 10-Piece Stainless Steel",
    price: 28,
    priceUnit: "set",
    minOrder: 50,
    minOrderUnit: "sets",
    image: "https://readdy.ai/api/search-image?query=garden%20tool%20set%2010%20piece%20stainless%20steel%20trowel%20rake%20hoe%20clean%20white%20background%20professional%20product%20photography%20outdoor%20gardening%20wholesale&width=400&height=300&seq=sfp802&orientation=landscape",
    category: "garden-tools",
    categoryIcon: "ri-plant-line",
    seller: "Hangzhou Garden Supply",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.33 Garden Industrial Park, Hangzhou, Zhejiang, China",
    sold: 18000,
    stock: 30000,
    description: "Complete 10-piece garden tool set with stainless steel heads and ergonomic rubber handles. Includes trowel, rake, hoe, pruner, gloves, and carry bag. Custom logo available.",
    rating: 4.7,
    reviews: 3450,
    badge: "Hot",
    specs: ["10-Piece Set", "Stainless Steel", "Ergonomic Grip", "Custom Logo"],
  },
  {
    id: 803,
    name: "Electric Hedge Trimmer 600W 55cm Blade",
    price: 65,
    priceUnit: "unit",
    minOrder: 10,
    minOrderUnit: "units",
    image: "https://readdy.ai/api/search-image?query=electric%20hedge%20trimmer%20600W%2055cm%20blade%20garden%20tool%20clean%20white%20background%20professional%20product%20photography%20outdoor%20power%20equipment&width=400&height=300&seq=sfp803&orientation=landscape",
    category: "garden-tools",
    categoryIcon: "ri-plant-line",
    seller: "Bosch Garden OEM",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.7 Power Tool Zone, Nanjing, Jiangsu, China",
    sold: 7800,
    stock: 5000,
    description: "Professional electric hedge trimmer with 600W motor and 55cm dual-action blade. Anti-vibration handle, safety switch, blade tip protector, 3m power cord, CE/GS certified.",
    rating: 4.6,
    reviews: 1890,
    badge: "New",
    specs: ["600W Motor", "55cm Blade", "Anti-Vibration", "CE/GS Certified"],
  },
  // Brass Hose Nozzles
  {
    id: 901,
    name: "Heavy Duty Brass Garden Hose Nozzle 8-Pattern",
    price: 3.8,
    priceUnit: "pc",
    minOrder: 100,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=heavy%20duty%20brass%20garden%20hose%20nozzle%208%20pattern%20spray%20gun%20shiny%20metal%20clean%20white%20background%20professional%20product%20photography%20garden%20watering%20tool&width=400&height=300&seq=sfp901&orientation=landscape",
    category: "hose-nozzle",
    categoryIcon: "ri-drop-line",
    seller: "Yuyao Brass Hardware Co.",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.18 Hardware Zone, Yuyao, Zhejiang, China",
    sold: 42000,
    stock: 200000,
    description: "Premium solid brass garden hose nozzle with 8 adjustable spray patterns. Ergonomic rubber grip, thumb control valve, fits all standard 3/4 inch hoses. Rust-proof, leak-proof design.",
    rating: 4.8,
    reviews: 8920,
    badge: "Top Seller",
    specs: ["8 Spray Patterns", "Solid Brass", "3/4\" Fit", "Rubber Grip"],
  },
  {
    id: 902,
    name: "Brass Hose Quick Connect Fitting Set 3/4 Inch",
    price: 2.2,
    priceUnit: "set",
    minOrder: 200,
    minOrderUnit: "sets",
    image: "https://readdy.ai/api/search-image?query=brass%20hose%20connector%20quick%20connect%20set%203%2F4%20inch%20garden%20hose%20fitting%20clean%20white%20background%20professional%20product%20photography%20plumbing%20hardware&width=400&height=300&seq=sfp902&orientation=landscape",
    category: "hose-nozzle",
    categoryIcon: "ri-drop-line",
    seller: "Cixi Plumbing Hardware",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.5 Plumbing Industrial Park, Cixi, Zhejiang, China",
    sold: 68000,
    stock: 500000,
    description: "Quick-connect brass hose fitting set for fast tool-free hose changes. Includes male and female connectors, O-ring sealed, 200 PSI rated, compatible with all standard garden hoses.",
    rating: 4.7,
    reviews: 14300,
    badge: "Hot",
    specs: ["Quick Connect", "200 PSI Rated", "O-Ring Sealed", "Universal Fit"],
  },
  {
    id: 903,
    name: "Brass High Pressure Adjustable Spray Nozzle",
    price: 5.5,
    priceUnit: "pc",
    minOrder: 50,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=brass%20adjustable%20spray%20nozzle%20high%20pressure%20garden%20hose%20gun%20clean%20white%20background%20professional%20product%20photography%20watering%20equipment%20wholesale&width=400&height=300&seq=sfp903&orientation=landscape",
    category: "hose-nozzle",
    categoryIcon: "ri-drop-line",
    seller: "Wenzhou Brass Works",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.22 Brass Street, Wenzhou, Zhejiang, China",
    sold: 29000,
    stock: 100000,
    description: "High-pressure adjustable brass spray nozzle for car washing, garden irrigation, and industrial cleaning. Twist-adjust from jet to mist, 300 PSI rated, chrome-plated finish.",
    rating: 4.6,
    reviews: 5670,
    badge: "New",
    specs: ["300 PSI Rated", "Twist Adjust", "Chrome Plated", "Car Wash Safe"],
  },
  // Sink & Bathroom Faucets
  {
    id: 1001,
    name: "Single Handle Kitchen Sink Faucet Brushed Nickel",
    price: 18.5,
    priceUnit: "pc",
    minOrder: 20,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=single%20handle%20kitchen%20sink%20faucet%20brushed%20nickel%20modern%20design%20clean%20white%20background%20professional%20product%20photography%20bathroom%20hardware%20wholesale&width=400&height=300&seq=sfp1001&orientation=landscape",
    category: "faucet",
    categoryIcon: "ri-water-flash-line",
    seller: "Kaiping Faucet Factory",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.88 Faucet Industrial Zone, Kaiping, Guangdong, China",
    sold: 31000,
    stock: 50000,
    description: "Modern single-handle kitchen faucet in brushed nickel finish. 360 degree swivel spout, ceramic disc cartridge for drip-free operation, easy single-hole installation, cUPC certified.",
    rating: 4.8,
    reviews: 6780,
    badge: "Top Seller",
    specs: ["360° Swivel", "Ceramic Disc", "cUPC Certified", "Easy Install"],
  },
  {
    id: 1002,
    name: "Bathroom Basin Waterfall Faucet Chrome OEM",
    price: 12.8,
    priceUnit: "pc",
    minOrder: 30,
    minOrderUnit: "pcs",
    image: "https://readdy.ai/api/search-image?query=bathroom%20basin%20faucet%20waterfall%20chrome%20modern%20luxury%20design%20clean%20white%20background%20professional%20product%20photography%20plumbing%20fixture%20wholesale&width=400&height=300&seq=sfp1002&orientation=landscape",
    category: "faucet",
    categoryIcon: "ri-water-flash-line",
    seller: "Zhongshan Sanitary Ware",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.12 Sanitary Zone, Zhongshan, Guangdong, China",
    sold: 24000,
    stock: 80000,
    description: "Luxury waterfall bathroom basin faucet with chrome finish. Single hole mount, cold/hot water mixing, ceramic valve, CUPC/CE certified, custom OEM branding available.",
    rating: 4.7,
    reviews: 4320,
    badge: "Hot",
    specs: ["Waterfall Flow", "Chrome Finish", "CUPC/CE", "OEM Available"],
  },
  {
    id: 1003,
    name: "Thermostatic Rain Shower Faucet System Set",
    price: 65,
    priceUnit: "set",
    minOrder: 10,
    minOrderUnit: "sets",
    image: "https://readdy.ai/api/search-image?query=thermostatic%20shower%20faucet%20set%20rain%20shower%20head%20chrome%20modern%20bathroom%20clean%20white%20background%20professional%20product%20photography%20plumbing%20wholesale&width=400&height=300&seq=sfp1003&orientation=landscape",
    category: "faucet",
    categoryIcon: "ri-water-flash-line",
    seller: "Foshan Shower Systems",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.9 Shower Industrial Park, Foshan, Guangdong, China",
    sold: 8900,
    stock: 20000,
    description: "Complete thermostatic shower system with 12-inch rain shower head, hand shower, and body jets. Constant temperature control, anti-scald protection, chrome finish, CE certified.",
    rating: 4.9,
    reviews: 2140,
    badge: "New",
    specs: ["Thermostatic", "12\" Rain Head", "Anti-Scald", "CE Certified"],
  },
  // Welding Cables
  {
    id: 1101,
    name: "Welding Cable 2/0 AWG 600V Copper Flexible",
    price: 2.8,
    priceUnit: "meter",
    minOrder: 100,
    minOrderUnit: "meters",
    image: "https://readdy.ai/api/search-image?query=heavy%20duty%20welding%20cable%202%2F0%20AWG%20copper%20flexible%20coil%20red%20black%20industrial%20electrical%20wire%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=sfp1101&orientation=landscape",
    category: "welding-cable",
    categoryIcon: "ri-plug-line",
    seller: "Baosheng Cable Group",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.1 Cable Industrial Park, Yangzhou, Jiangsu, China",
    sold: 85000,
    stock: 500000,
    description: "Premium 2/0 AWG welding cable with 100% copper strands for maximum conductivity. 600V rated, -40C to +90C operating range, oil and abrasion resistant EPDM jacket, UL/CSA listed.",
    rating: 4.9,
    reviews: 12400,
    badge: "Top Seller",
    specs: ["2/0 AWG", "600V Rated", "100% Copper", "UL/CSA Listed"],
  },
  {
    id: 1102,
    name: "Welding Cable Set 4 AWG with Clamps 25ft",
    price: 38,
    priceUnit: "set",
    minOrder: 20,
    minOrderUnit: "sets",
    image: "https://readdy.ai/api/search-image?query=welding%20cable%20set%204%20AWG%20with%20heavy%20duty%20clamps%2025ft%20red%20black%20coil%20clean%20white%20background%20professional%20product%20photography%20electrical%20equipment%20wholesale&width=400&height=300&seq=sfp1102&orientation=landscape",
    category: "welding-cable",
    categoryIcon: "ri-plug-line",
    seller: "Delixi Electric Co.",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.5 Electric Avenue, Wenzhou, Zhejiang, China",
    sold: 19000,
    stock: 50000,
    description: "Complete 4 AWG welding cable set with heavy-duty copper clamps. 25ft length, 400A capacity, color-coded red/black, twist-lock connectors, suitable for MIG, TIG, and stick welding.",
    rating: 4.7,
    reviews: 3890,
    badge: "Hot",
    specs: ["4 AWG", "400A Capacity", "25ft Length", "Copper Clamps"],
  },
  {
    id: 1103,
    name: "Rubber Welding Cable 1/0 AWG High Flexibility",
    price: 1.95,
    priceUnit: "meter",
    minOrder: 200,
    minOrderUnit: "meters",
    image: "https://readdy.ai/api/search-image?query=rubber%20welding%20cable%201%2F0%20AWG%20high%20flexibility%20coiled%20black%20industrial%20electrical%20wire%20clean%20white%20background%20professional%20product%20photography%20wholesale&width=400&height=300&seq=sfp1103&orientation=landscape",
    category: "welding-cable",
    categoryIcon: "ri-plug-line",
    seller: "Zhengzhou Cable Works",
    sellerFlag: "🇨🇳",
    sellerCountry: "China",
    factoryAddress: "No.33 Cable Road, Zhengzhou, Henan, China",
    sold: 62000,
    stock: 1000000,
    description: "Ultra-flexible 1/0 AWG rubber welding cable for professional welding applications. Fine-stranded copper conductor, neoprene rubber jacket, excellent kink resistance, custom lengths available.",
    rating: 4.8,
    reviews: 8760,
    badge: "New",
    specs: ["1/0 AWG", "Neoprene Jacket", "Fine-Stranded", "Custom Length"],
  },
];
